import React, { useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { FilterMatchMode, FilterOperator } from 'primereact/api'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import {
  DataTable,
  DataTableFilterMeta,
  DataTableFilterMetaData,
} from 'primereact/datatable'

import { Header } from '../components/Header'
import { SearchField } from '../components/SearchField'
import { Tenant } from '../data/models'
import { useAllTenants } from '../data/tenants'
import FallbackErrorMessage from '../utils/errors/FallbackMessage'
import {
  setCurrentTenantConfig,
  useCurrentTenantConfig,
} from '../utils/tenants'

export const SetTenantPage: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { data } = useAllTenants()
  const { tenantId: currentTenantId } = useCurrentTenantConfig()

  // search and filters
  const [filters, setFilters] = useState<
    DataTableFilterMeta | null | undefined
  >(null) // no filters are set
  const [globalFilterValue, setGlobalFilterValue] = useState<string>('') // no value in searchfield

  // clear the filters on start so there are empty
  useEffect(() => {
    initFilters()
  }, [])

  // set filters
  const onGlobalFilterChange = (value: string) => {
    let _filters: DataTableFilterMeta = { ...filters }
    let global = _filters['global'] as DataTableFilterMetaData
    global.value = value

    _filters = { ..._filters, global: global }

    setFilters(_filters)
    setGlobalFilterValue(value)
  }

  // resest filters
  const initFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      Name: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },
      Description: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
      },
    })
    setGlobalFilterValue('')
  }

  // select button
  const selectBodyTemplate = (rowData: Tenant) => {
    if (rowData.id === currentTenantId) {
      return (
        <Button
          label={t('tenant.table.columns.actions.selected.label')}
          disabled={rowData.id === currentTenantId}
          severity="success"
          onClick={() => {
            setCurrentTenantConfig({ tenantId: rowData.id })
            navigate('/')
          }}
          style={{ width: '150px' }}
        />
      )
    }
    if (rowData.ismaintenanceenabled === true) {
      return (
        <Button
          label={t('tenant.table.columns.actions.maintenance.label')}
          disabled={
            rowData.id === currentTenantId ||
            rowData.ismaintenanceenabled === true
          }
          severity="secondary"
          onClick={() => {
            setCurrentTenantConfig({ tenantId: rowData.id })
            navigate('/')
          }}
          style={{
            width: '150px',
          }}
        />
      )
    }
    return (
      <Button
        label={t('tenant.table.columns.actions.select.label')}
        disabled={rowData.id === currentTenantId}
        icon="pi pi-chevron-right"
        iconPos="right"
        onClick={() => {
          setCurrentTenantConfig({ tenantId: rowData.id })
          navigate('/')
        }}
        style={{ width: '150px' }}
      />
    )
  }

  const actions = (
    <SearchField
      searchValue={globalFilterValue}
      setSearchValue={onGlobalFilterChange}
      placeholder={t('tenant.title.search.placeholder')}
    />
  )
  return (
    <>
      <Header title={t('tenant.title.title')} actions={actions} />
      <ErrorBoundary FallbackComponent={FallbackErrorMessage}>
        <div className="mt-6">
          <DataTable
            value={data}
            dataKey="Id"
            sortField="Name"
            sortOrder={1}
            filters={filters ? filters : undefined}
            globalFilterFields={['Name', 'Description']}
            showGridlines
          >
            {/* Name */}
            <Column
              field="name"
              header={t('tenant.table.columns.name.header')}
              sortable
              filter
              filterPlaceholder={t(
                'tenant.table.columns.name.filterPlaceholder',
              )}
              filterMenuStyle={{ minWidth: '16em' }}
            ></Column>

            {/* Description */}
            <Column
              field="description"
              header={t('tenant.table.columns.description.header')}
              filter
              filterPlaceholder={t(
                'tenant.table.columns.description.filterPlaceholder',
              )}
              filterMenuStyle={{ minWidth: '16em' }}
            ></Column>

            {/* Select button */}
            <Column body={selectBodyTemplate}></Column>
          </DataTable>
        </div>
      </ErrorBoundary>
    </>
  )
}
